import { toArrayBuffer } from "@/lib/utils/array-buffer";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { sha256Hex } from "@/lib/utils/hash";
import { formatDateBR, formatDateTimeBR, formatPetAge } from "@/lib/date-utils";
import { getProntuarioActionLabel } from "@/lib/prontuario/action-labels";
import i18n from "@/i18n";
import { buildPosologySummary } from "@/lib/prescriptions/posology";

interface BuildPdfParams {
  record: any;
  exportId?: string;
  verificationUrl?: string;
  vaccinations?: Array<{
    application_date?: string | null;
    dose_number?: number | null;
    batch_number?: string | null;
    expiry_date?: string | null;
    next_dose_date?: string | null;
    notes?: string | null;
    adverse_reaction?: string | null;
    items?: { name?: string | null; description?: string | null } | null;
  }>;
  examResults?: Array<{
    exam_type?: string | null;
    created_at?: string | null;
    observations?: string | null;
    interpretation?: string | null;
    results_json?: unknown;
  }>;
  prescriptions?: Array<{
    type?: string | null;
    issued_at?: string | null;
    observations?: string | null;
    validity_in_days?: number | null;
    manipulated_payload?: unknown;
    medical_prescription_items?: Array<{
      name?: string | null;
      medication_name?: string | null;
      medication_concentration?: string | null;
      presentation?: string | null;
      posology_mode?: string | null;
      free_text_posology?: string | null;
      dose_amount?: string | null;
      dose_unit?: string | null;
      frequency_value?: string | null;
      frequency_unit?: string | null;
      duration_value?: string | null;
      duration_unit?: string | null;
      route?: string | null;
      administration_route?: string | null;
      frequency?: string | null;
      duration?: string | null;
      notes?: string | null;
      instructions?: string | null;
    }>;
    medical_prescription_exam_items?: Array<{
      exam_name?: string | null;
      instructions?: string | null;
    }>;
  }>;
  versions?: Array<{
    acao: string;
    tipo: string;
    created_at: string;
    hash_conteudo?: string | null;
    autor?: { full_name?: string | null } | null;
    ip?: string | null;
    conteudo_snapshot?: unknown;
  }>;
  events?: Array<{
    acao: string;
    created_at: string;
    hash_ref?: string | null;
    user?: { full_name?: string | null } | null;
    ip?: string | null;
  }>;
}

const PAGE_MARGIN = 40;
const LINE_GAP = 6;

const addBlock = (
  page: any,
  text: string,
  options: { x: number; y: number; size?: number; font: any },
) => {
  page.drawText(text, {
    x: options.x,
    y: options.y,
    size: options.size ?? 12,
    font: options.font,
  });
};

const normalizeValue = (value: unknown) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return value.toString();
  return JSON.stringify(value);
};

const hasValue = (value: unknown) => normalizeValue(value) !== "";

const getSimplifiedManipulatedPayload = (payload: unknown) => {
  if (!payload || typeof payload !== "object") return null;
  const payloadObj = payload as Record<string, unknown>;
  const components = Array.isArray(payloadObj.components) ? payloadObj.components : [];
  const hasConcentrationField = components.some(
    (component) => component && typeof component === "object" && "concentration" in (component as Record<string, unknown>),
  );
  const hasNewShape =
    hasConcentrationField ||
    "treatment_days" in payloadObj ||
    "total_doses" in payloadObj;
  if (!hasNewShape) return null;
  const normalizedComponents = components
    .map((component) => {
      if (!component || typeof component !== "object") {
        return { name: "", concentration: "" };
      }
      const comp = component as Record<string, unknown>;
      return {
        name: normalizeValue(comp.name),
        concentration: normalizeValue(comp.concentration),
      };
    })
    .filter((component) => component.name || component.concentration);
  const treatmentDays = normalizeValue(payloadObj.treatment_days);
  const totalDoses = normalizeValue(payloadObj.total_doses);
  const route = normalizeValue(payloadObj.route);

  if (
    normalizedComponents.length === 0 &&
    !treatmentDays &&
    !totalDoses &&
    !route
  ) {
    return null;
  }

  return {
    components: normalizedComponents,
    treatmentDays,
    totalDoses,
    route,
  };
};

// Mapeamento de campos para nomes legíveis
const FIELD_NAMES: Record<string, string> = {
  chief_complaint: i18n.t("medicalRecords:form.chiefComplaint"),
  history: i18n.t("medicalRecords:form.history"),
  behavior: i18n.t("medicalRecords:form.behavior"),
  diet: i18n.t("medicalRecords:form.diet"),
  environment: i18n.t("medicalRecords:form.environment"),
  presumptive_diagnosis: i18n.t("medicalRecords:print.presumptiveDiagnosis", { defaultValue: "Diagnóstico Presuntivo" }),
  definitive_diagnosis: i18n.t("medicalRecords:print.definitiveDiagnosis", { defaultValue: "Diagnóstico Definitivo" }),
  treatment: i18n.t("medicalRecords:form.treatmentConduct"),
  weight: i18n.t("medicalRecords:form.weight"),
  temperature: i18n.t("medicalRecords:form.temperature"),
  heart_rate: i18n.t("medicalRecords:form.heartRate"),
  respiratory_rate: i18n.t("medicalRecords:form.respiratoryRate"),
  mucous_membranes: i18n.t("medicalRecords:form.mucousMembranes"),
  capillary_refill_time: i18n.t("medicalRecords:form.crt"),
  hydration_status: i18n.t("medicalRecords:form.hydrationStatus"),
  lymph_nodes: i18n.t("medicalRecords:form.lymphNodes"),
  cardiac_auscultation: i18n.t("medicalRecords:form.cardiacAuscultation"),
  respiratory_auscultation: i18n.t("medicalRecords:form.respiratoryAuscultation"),
  abdominal_palpation: i18n.t("medicalRecords:form.abdominalPalpation"),
  locomotor_system: i18n.t("medicalRecords:form.locomotorSystem"),
  skin_coat: i18n.t("medicalRecords:form.skinCoat"),
  other_systems: i18n.t("medicalRecords:form.otherSystems"),
  vet_user_id: i18n.t("reports:vaccines.history.veterinarian"),
  pet_id: i18n.t("prescriptions:print.patient"),
  clinic_id: i18n.t("prescriptions:print.clinic"),
  appointment_id: i18n.t("medicalRecords:print.appointment", { defaultValue: "Agendamento" }),
  consultation_date: i18n.t("medicalRecords:print.consultationDate"),
};

const TXT = {
  empty: i18n.t("medicalRecords:print.emptyValue", { defaultValue: "—" }),
  emptyAudit: i18n.t("medicalRecords:actions.exportNoChanges"),
  user: i18n.t("medicalRecords:history.userFallback"),
  title: i18n.t("medicalRecords:print.title"),
  consultationDate: i18n.t("medicalRecords:print.consultationDate"),
  patient: i18n.t("prescriptions:print.patient"),
  clinic: i18n.t("prescriptions:print.clinic"),
  name: i18n.t("medicalRecords:print.name"),
  species: i18n.t("medicalRecords:print.species"),
  breed: i18n.t("medicalRecords:print.breed"),
  age: i18n.t("medicalRecords:print.age", { defaultValue: "Idade" }),
  weight: i18n.t("medicalRecords:print.weight"),
  owner: i18n.t("medicalRecords:print.owner"),
  phone: i18n.t("medicalRecords:print.phone"),
  anamnesis: i18n.t("medicalRecords:form.tabs.anamnesis"),
  physicalExam: i18n.t("medicalRecords:form.tabs.physicalExam"),
  diagnosis: i18n.t("medicalRecords:form.tabs.diagnosis"),
  treatment: i18n.t("medicalRecords:form.tabs.treatment"),
  presumptive: i18n.t("medicalRecords:print.presumptive", { defaultValue: "Presuntivo" }),
  definitive: i18n.t("medicalRecords:print.definitive", { defaultValue: "Definitivo" }),
  applications: i18n.t("reports:vaccines.history.title"),
  applicationDefault: i18n.t("reports:vaccines.history.defaultItem"),
  dose: i18n.t("reports:vaccines.history.dose"),
  batch: i18n.t("reports:vaccines.history.batch"),
  expiry: i18n.t("reports:vaccines.form.batchExpiry"),
  nextDose: i18n.t("reports:vaccines.history.nextDose"),
  adverseReaction: i18n.t("reports:vaccines.form.adverseReaction"),
  observations: i18n.t("prescriptions:print.observations"),
  examsPerformed: i18n.t("medicalRecords:print.examsPerformed", { defaultValue: "EXAMES REALIZADOS" }),
  exam: i18n.t("prescriptions:print.exams", { defaultValue: "Exame" }),
  interpretation: i18n.t("medicalRecords:print.interpretation", { defaultValue: "Interpretação" }),
  prescriptions: i18n.t("medicalRecords:print.prescriptions", { defaultValue: "PRESCRIÇÕES" }),
  prescriptionLabel: i18n.t("medicalRecords:print.prescriptionLabel", { defaultValue: "Prescrição" }),
  medication: i18n.t("prescriptions:composer.medication", { defaultValue: "Medicamento" }),
  composition: i18n.t("prescriptions:manipulated.formulaTitle", { defaultValue: "Composição" }),
  treatmentDays: i18n.t("prescriptions:manipulated.treatmentDays", { defaultValue: "Tempo de tratamento (dias)" }),
  totalDoses: i18n.t("prescriptions:manipulated.totalDoses", { defaultValue: "Quantidade total de doses" }),
  route: i18n.t("prescriptions:print.route"),
  posology: i18n.t("medicalRecords:print.posology", { defaultValue: "Posologia" }),
  finalProduct: i18n.t("medicalRecords:print.finalProduct", { defaultValue: "Produto final" }),
  pharmacy: i18n.t("medicalRecords:print.pharmacy", { defaultValue: "Farmácia" }),
  instructions: i18n.t("prescriptions:print.instructions"),
  presentation: i18n.t("prescriptions:print.presentation"),
  dosage: i18n.t("prescriptions:print.dosage"),
  frequency: i18n.t("prescriptions:print.frequency"),
  duration: i18n.t("prescriptions:print.duration"),
  changesHistory: i18n.t("medicalRecords:print.changesHistory", { defaultValue: "HISTÓRICO DE ALTERAÇÕES" }),
  exportVerification: i18n.t("medicalRecords:print.exportVerification", { defaultValue: "VERIFICAÇÃO DA EXPORTAÇÃO" }),
  exportVerificationText: i18n.t("medicalRecords:print.exportVerificationText", { defaultValue: "Este documento pode ser verificado pelo ID da exportação." }),
  exportId: i18n.t("medicalRecords:exportDialog.exportId"),
  verifyAt: i18n.t("medicalRecords:print.verifyAt", { defaultValue: "Verifique em:" }),
  validateIntegrity: i18n.t("medicalRecords:print.validateIntegrity", { defaultValue: "Para validar a integridade:" }),
  validateStep1: i18n.t("medicalRecords:print.validateStep1", { defaultValue: "1. Acesse a página de verificação" }),
  validateStep2: i18n.t("medicalRecords:print.validateStep2", { defaultValue: "2. Envie este PDF para comparar o hash registrado" }),
  exportedAt: i18n.t("medicalRecords:print.exportedAt", { defaultValue: "Documento exportado em" }),
  signatureStamp: i18n.t("prescriptions:print.signatureStamp"),
};

// Função auxiliar para resolver IDs em nomes legíveis
function resolveIdToName(
  field: string,
  id: string | null | undefined,
  record: any
): string {
  if (!id) return TXT.empty;

  const idStr = normalizeValue(id);
  if (!idStr) return TXT.empty;

  if (field === "vet_user_id") {
    const vet = record?.profiles;
    if (vet) {
      const name = vet.full_name || TXT.user;
      const crmvState = vet.crmv_state;
      const crmvNumber = vet.crmv_number;
      const crmv = crmvState && crmvNumber
        ? `${crmvState}${crmvNumber}`
        : vet.crmv || "";
      return crmv ? `${name} (CRMV: ${crmv})` : name;
    }
    return i18n.t("medicalRecords:print.userWithId", { user: TXT.user, id: idStr });
  }

  if (field === "pet_id") {
    return record?.pets?.name || i18n.t("medicalRecords:print.entityWithId", { entity: TXT.patient, id: idStr });
  }

  if (field === "clinic_id") {
    return record?.clinics?.name || i18n.t("medicalRecords:print.entityWithId", { entity: TXT.clinic, id: idStr });
  }

  if (field === "appointment_id") {
    return i18n.t("medicalRecords:print.entityWithId", { entity: FIELD_NAMES.appointment_id, id: idStr });
  }

  return idStr;
}

// Função para comparar duas versões e identificar mudanças
function compareVersions(
  oldSnapshot: Record<string, unknown>,
  newSnapshot: Record<string, unknown>,
  record: any
): Array<{ field: string; fieldName: string; oldValue: string; newValue: string }> {
  const changes: Array<{ field: string; fieldName: string; oldValue: string; newValue: string }> = [];
  const allFields = new Set([...Object.keys(oldSnapshot), ...Object.keys(newSnapshot)]);

  // Campos de ID que devem ser convertidos para nomes
  const idFields = ["vet_user_id", "pet_id", "clinic_id", "appointment_id"];

  allFields.forEach((field) => {
    // Ignorar campos de metadados
    if (field === "id" || field === "created_at" || field === "updated_at" || field === "deleted_at") {
      return;
    }

    const oldVal = oldSnapshot[field];
    const newVal = newSnapshot[field];

    // Se é um campo de ID, converter para nome
    let oldStr: string;
    let newStr: string;

    if (idFields.includes(field)) {
      oldStr = resolveIdToName(field, oldVal as string | null | undefined, record);
      newStr = resolveIdToName(field, newVal as string | null | undefined, record);
    } else if (field === "consultation_date") {
      // Formatar data da consulta
      oldStr = formatDateBR(oldVal as string | null | undefined) || TXT.empty;
      newStr = formatDateBR(newVal as string | null | undefined) || TXT.empty;
    } else {
      oldStr = normalizeValue(oldVal);
      newStr = normalizeValue(newVal);
    }

    // Se ambos estão vazios, ignorar
    if ((!oldStr || oldStr === TXT.empty) && (!newStr || newStr === TXT.empty)) return;

    // Se mudou
    if (oldStr !== newStr) {
      changes.push({
        field,
        fieldName: FIELD_NAMES[field] || field,
        oldValue: oldStr || TXT.empty,
        newValue: newStr || TXT.empty,
      });
    }
  });

  return changes;
}

// Função para gerar audit log a partir das versões
function generateAuditLog(
  versions: Array<{
    conteudo_snapshot?: unknown;
    created_at: string;
    autor?: { full_name?: string | null } | null;
    acao: string;
  }>,
  record: any
): Array<{ date: string; author: string; action: string; changes: string[] }> {
  if (!versions || versions.length === 0) return [];

  const auditLog: Array<{ date: string; author: string; action: string; changes: string[] }> = [];
  const idFields = ["vet_user_id", "pet_id", "clinic_id", "appointment_id"];

  // Ordenar versões por data (mais antiga primeiro)
  const sortedVersions = [...versions].sort((a, b) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  for (let i = 0; i < sortedVersions.length; i++) {
    const version = sortedVersions[i];
    const snapshot = version.conteudo_snapshot;

    if (!snapshot || typeof snapshot !== "object") continue;

    // Pular entradas de criação (CREATE)
    if (i === 0 || version.acao === "CREATE") {
      continue;
    }

    const snapshotObj = snapshot as Record<string, unknown>;
    const changes: string[] = [];

    // Versões subsequentes - comparar com a anterior
    const prevSnapshot = sortedVersions[i - 1].conteudo_snapshot;
    if (prevSnapshot && typeof prevSnapshot === "object") {
      const prevSnapshotObj = prevSnapshot as Record<string, unknown>;
      const versionChanges = compareVersions(prevSnapshotObj, snapshotObj, record);

      versionChanges.forEach((change) => {
        if (change.oldValue === TXT.empty) {
          changes.push(`${change.fieldName} ${i18n.t("medicalRecords:print.changeAdded", { defaultValue: "foi adicionado" })}: ${change.newValue}`);
        } else if (change.newValue === TXT.empty) {
          changes.push(`${change.fieldName} ${i18n.t("medicalRecords:print.changeRemoved", { defaultValue: "foi removido" })} (${i18n.t("medicalRecords:print.previously", { defaultValue: "anteriormente" })}: ${change.oldValue})`);
        } else {
          changes.push(`${change.fieldName}: "${change.oldValue}" ${i18n.t("medicalRecords:print.changedTo", { defaultValue: "virou" })} "${change.newValue}"`);
        }
      });
    }

    if (changes.length > 0) {
      const actionName = getProntuarioActionLabel(version.acao);
      auditLog.push({
        date: formatDateTimeBR(version.created_at) || version.created_at,
        author: version.autor?.full_name || TXT.user,
        action: actionName,
        changes,
      });
    }
  }

  return auditLog;
}

const wrapText = (text: string, maxWidth: number, font: any, size: number) => {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  });

  if (current) lines.push(current);
  return lines;
};

export async function buildProntuarioPdf({
  record,
  exportId,
  verificationUrl,
  vaccinations = [],
  examResults = [],
  prescriptions = [],
  versions = [],
  events = [],
}: BuildPdfParams) {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  let page = pdfDoc.addPage();
  const { height, width } = page.getSize();
  const maxWidth = width - PAGE_MARGIN * 2;
  let cursor = height - PAGE_MARGIN;

  const ensureSpace = (lines = 1, size = 12) => {
    const needed = lines * (size + LINE_GAP);
    if (cursor - needed < PAGE_MARGIN) {
      page = pdfDoc.addPage();
      cursor = height - PAGE_MARGIN;
    }
  };

  const drawLine = (text: string, boldLine = false, size = 12, center = false) => {
    ensureSpace(1, size);
    const textWidth = (boldLine ? bold : font).widthOfTextAtSize(text, size);
    const x = center ? (width - textWidth) / 2 : PAGE_MARGIN;
    addBlock(page, text, { x, y: cursor, size, font: boldLine ? bold : font });
    cursor -= size + LINE_GAP;
  };

  const drawWrapped = (text: string, boldLine = false, size = 12, indent = 0) => {
    const lines = wrapText(text, maxWidth - indent, boldLine ? bold : font, size);
    ensureSpace(lines.length, size);
    lines.forEach((line) => {
      addBlock(page, line, { x: PAGE_MARGIN + indent, y: cursor, size, font: boldLine ? bold : font });
      cursor -= size + LINE_GAP;
    });
  };

  const drawKeyValue = (label: string, value: unknown, indent = 0) => {
    if (!hasValue(value)) return;
    const text = `${label}: ${normalizeValue(value)}`;
    drawWrapped(text, false, 12, indent);
  };

  const drawDivider = () => {
    ensureSpace(1, 12);
    cursor -= 8;
    page.drawLine({
      start: { x: PAGE_MARGIN, y: cursor },
      end: { x: width - PAGE_MARGIN, y: cursor },
      thickness: 1,
      color: rgb(0, 0, 0),
      opacity: 0.3,
    });
    cursor -= 8;
  };

  const drawSectionTitle = (title: string) => {
    cursor -= 8;
    drawLine(title, true, 14);
    cursor -= 4;
  };

  // Preparar dados
  const ownerPhones = record?.pets?.owners?.owners_phones ?? [];
  const ownerPhone =
    ownerPhones.find((phone: { is_primary?: boolean | null }) => phone.is_primary)
      ?.phone_number ?? ownerPhones[0]?.phone_number;
  const crmvFromProfile = record?.profiles?.crmv_state && record?.profiles?.crmv_number
    ? `${record.profiles.crmv_state} ${record.profiles.crmv_number}`
    : null;
  const vetName = record?.vet_name ?? record?.profiles?.full_name;
  const vetCrmv = record?.vet_crmv ?? crmvFromProfile ?? record?.crmv;

  // Título principal
  drawLine(TXT.title, true, 18, true);
  const consultationDate = formatDateTimeBR(record?.created_at);
  if (consultationDate && consultationDate !== "-") {
    drawLine(`${TXT.consultationDate}: ${consultationDate.replace(" ", " às ")}`, false, 11, true);
  }
  cursor -= 8;
  drawDivider();
  cursor -= 4;
  // Identificação do Paciente
  drawSectionTitle(TXT.patient);
  if (hasValue(record?.pets?.name)) {
    drawKeyValue(TXT.name, record.pets.name);
  }
  if (hasValue(record?.pets?.species?.name)) {
    drawKeyValue(TXT.species, record.pets.species.name);
  }
  if (hasValue(record?.pets?.breeds?.name)) {
    drawKeyValue(TXT.breed, record.pets.breeds.name);
  }
  const petAge = formatPetAge(record?.pets?.birth_date);
  if (petAge) {
    drawKeyValue(TXT.age, petAge);
  }
  if (hasValue(record?.weight)) {
    drawKeyValue(TXT.weight, `${record.weight} kg`);
  }
  if (hasValue(record?.pets?.owners?.name)) {
    drawKeyValue(TXT.owner, record.pets.owners.name);
  }
  if (hasValue(ownerPhone)) {
    drawKeyValue(TXT.phone, ownerPhone);
  }
  drawDivider();
  cursor -= 4;
  // Anamnese
  const hasAnamnesis = hasValue(record?.chief_complaint) ||
    hasValue(record?.history) ||
    hasValue(record?.behavior) ||
    hasValue(record?.diet) ||
    hasValue(record?.environment);

  if (hasAnamnesis) {
    drawSectionTitle(TXT.anamnesis);
    if (hasValue(record?.chief_complaint)) {
      drawWrapped(`${FIELD_NAMES.chief_complaint}:`, true, 12);
      drawWrapped(normalizeValue(record.chief_complaint), false, 12, 16);
      cursor -= 4;
    }
    if (hasValue(record?.history)) {
      drawWrapped(`${FIELD_NAMES.history}:`, true, 12);
      drawWrapped(normalizeValue(record.history), false, 12, 16);
      cursor -= 4;
    }
    if (hasValue(record?.behavior)) {
      drawWrapped(`${FIELD_NAMES.behavior}:`, true, 12);
      drawWrapped(normalizeValue(record.behavior), false, 12, 16);
      cursor -= 4;
    }
    if (hasValue(record?.diet)) {
      drawWrapped(`${FIELD_NAMES.diet}:`, true, 12);
      drawWrapped(normalizeValue(record.diet), false, 12, 16);
      cursor -= 4;
    }
    if (hasValue(record?.environment)) {
      drawWrapped(`${FIELD_NAMES.environment}:`, true, 12);
      drawWrapped(normalizeValue(record.environment), false, 12, 16);
      cursor -= 4;
    }
  }

  // Exame Físico
  const hasPhysicalExam = hasValue(record?.weight) ||
    hasValue(record?.temperature) ||
    hasValue(record?.heart_rate) ||
    hasValue(record?.respiratory_rate) ||
    hasValue(record?.mucous_membranes) ||
    hasValue(record?.capillary_refill_time) ||
    hasValue(record?.hydration_status) ||
    hasValue(record?.lymph_nodes) ||
    hasValue(record?.cardiac_auscultation) ||
    hasValue(record?.respiratory_auscultation) ||
    hasValue(record?.abdominal_palpation) ||
    hasValue(record?.locomotor_system) ||
    hasValue(record?.skin_coat) ||
    hasValue(record?.other_systems);

  if (hasPhysicalExam) {
    cursor -= 4;
    drawDivider();
    cursor -= 4;
    drawSectionTitle(TXT.physicalExam);
    if (hasValue(record?.weight)) {
      drawKeyValue(TXT.weight, `${record.weight} kg`);
    }
    if (hasValue(record?.temperature)) {
      drawKeyValue(FIELD_NAMES.temperature, `${record.temperature}°C`);
    }
    if (hasValue(record?.heart_rate)) {
      drawKeyValue(FIELD_NAMES.heart_rate, `${record.heart_rate} bpm`);
    }
    if (hasValue(record?.respiratory_rate)) {
      drawKeyValue(FIELD_NAMES.respiratory_rate, `${record.respiratory_rate} mpm`);
    }
    if (hasValue(record?.mucous_membranes)) {
      drawKeyValue(FIELD_NAMES.mucous_membranes, record.mucous_membranes);
    }
    if (hasValue(record?.capillary_refill_time)) {
      drawKeyValue(FIELD_NAMES.capillary_refill_time, record.capillary_refill_time);
    }
    if (hasValue(record?.hydration_status)) {
      drawKeyValue(FIELD_NAMES.hydration_status, record.hydration_status);
    }
    if (hasValue(record?.lymph_nodes)) {
      drawKeyValue(FIELD_NAMES.lymph_nodes, record.lymph_nodes);
    }
    if (hasValue(record?.cardiac_auscultation)) {
      drawKeyValue(FIELD_NAMES.cardiac_auscultation, record.cardiac_auscultation);
    }
    if (hasValue(record?.respiratory_auscultation)) {
      drawKeyValue(FIELD_NAMES.respiratory_auscultation, record.respiratory_auscultation);
    }
    if (hasValue(record?.abdominal_palpation)) {
      drawKeyValue(FIELD_NAMES.abdominal_palpation, record.abdominal_palpation);
    }
    if (hasValue(record?.locomotor_system)) {
      drawKeyValue(FIELD_NAMES.locomotor_system, record.locomotor_system);
    }
    if (hasValue(record?.skin_coat)) {
      drawKeyValue(FIELD_NAMES.skin_coat, record.skin_coat);
    }
    if (hasValue(record?.other_systems)) {
      drawKeyValue(FIELD_NAMES.other_systems, record.other_systems);
    }
  }

  // Diagnóstico
  const hasDiagnosis = hasValue(record?.presumptive_diagnosis) || hasValue(record?.definitive_diagnosis);
  if (hasDiagnosis) {
    cursor -= 4;
    drawDivider();
    cursor -= 4;
    drawSectionTitle(TXT.diagnosis);
    if (hasValue(record?.presumptive_diagnosis)) {
      drawWrapped(`${TXT.presumptive}:`, true, 12);
      drawWrapped(normalizeValue(record.presumptive_diagnosis), false, 12, 16);
      cursor -= 4;
    }
    if (hasValue(record?.definitive_diagnosis)) {
      drawWrapped(`${TXT.definitive}:`, true, 12);
      drawWrapped(normalizeValue(record.definitive_diagnosis), false, 12, 16);
      cursor -= 4;
    }
  }

  // Tratamento
  if (hasValue(record?.treatment)) {
    cursor -= 4;
    drawDivider();
    cursor -= 4;
    drawSectionTitle(TXT.treatment);
    drawWrapped(normalizeValue(record.treatment), false, 12);
  }

  // Vacinas Aplicadas
  if (vaccinations.length > 0) {
    cursor -= 4;
    drawDivider();
    cursor -= 4;
    drawSectionTitle(TXT.applications);
    vaccinations.forEach((vac, idx) => {
      if (idx > 0) cursor -= 4;
      const vacName = vac.items?.name || TXT.applicationDefault;
      const vacDate = formatDateBR(vac.application_date);
      drawWrapped(`${idx + 1}. ${vacName}${vacDate && vacDate !== "-" ? ` - ${vacDate}` : ""}`, true, 12);
      if (hasValue(vac.dose_number)) {
        drawKeyValue(TXT.dose, vac.dose_number, 16);
      }
      if (hasValue(vac.batch_number)) {
        drawKeyValue(TXT.batch, vac.batch_number, 16);
      }
      if (hasValue(vac.expiry_date)) {
        const expiryDate = formatDateBR(vac.expiry_date);
        if (expiryDate && expiryDate !== "-") {
          drawKeyValue(TXT.expiry, expiryDate, 16);
        }
      }
      if (hasValue(vac.next_dose_date)) {
        const nextDate = formatDateBR(vac.next_dose_date);
        if (nextDate && nextDate !== "-") {
          drawKeyValue(TXT.nextDose, nextDate, 16);
        }
      }
      if (hasValue(vac.adverse_reaction)) {
        drawKeyValue(TXT.adverseReaction, vac.adverse_reaction, 16);
      }
      if (hasValue(vac.notes)) {
        drawKeyValue(TXT.observations, vac.notes, 16);
      }
    });
  }

  // Exames Realizados
  if (examResults.length > 0) {
    cursor -= 8;
    drawDivider();
    cursor -= 4;
    drawSectionTitle(TXT.examsPerformed);
    examResults.forEach((exam, idx) => {
      if (idx > 0) cursor -= 8;
      const examType = exam.exam_type || TXT.exam;
      const examDate = formatDateBR(exam.created_at);
      drawWrapped(`${examType}${examDate && examDate !== "-" ? ` - ${examDate}` : ""}`, true, 12);
      if (hasValue(exam.interpretation)) {
        drawWrapped(`${TXT.interpretation}:`, true, 12, 16);
        drawWrapped(normalizeValue(exam.interpretation), false, 12, 16);
        cursor -= 4;
      }
      if (hasValue(exam.observations)) {
        drawWrapped(`${TXT.observations}:`, true, 12, 16);
        drawWrapped(normalizeValue(exam.observations), false, 12, 16);
        cursor -= 4;
      }
    });
  }

  // Prescrições
  if (prescriptions.length > 0) {
    cursor -= 8;
    drawDivider();
    cursor -= 4;
    drawSectionTitle(TXT.prescriptions);
    prescriptions.forEach((presc, idx) => {
      if (idx > 0) cursor -= 8;
      const prescDate = formatDateTimeBR(presc.issued_at);
      drawWrapped(`${TXT.prescriptionLabel} #${idx + 1}${prescDate && prescDate !== "-" ? ` - ${prescDate.replace(" ", " às ")}` : ""}`, true, 12);

      if (presc.type === "manipulated" && presc.manipulated_payload) {
        // Prescrição manipulada
        const payload = presc.manipulated_payload;
        if (payload && typeof payload === "object") {
          const simplified = getSimplifiedManipulatedPayload(payload);

          if (simplified) {
            drawWrapped(`${TXT.composition}:`, true, 12, 16);
            if (simplified.components.length > 0) {
              simplified.components.forEach((component, compIdx) => {
                const compText = `${compIdx + 1}. ${component.name || TXT.empty}${component.concentration ? ` — ${component.concentration}` : ""}`;
                drawWrapped(compText, false, 11, 32);
              });
            } else {
              drawWrapped(TXT.empty, false, 11, 32);
            }
            cursor -= 2;

            drawWrapped(`${TXT.treatmentDays}:`, true, 12, 16);
            drawWrapped(simplified.treatmentDays || TXT.empty, false, 11, 32);
            cursor -= 2;

            drawWrapped(`${TXT.totalDoses}:`, true, 12, 16);
            drawWrapped(simplified.totalDoses || TXT.empty, false, 11, 32);
            cursor -= 2;

            drawWrapped(`${TXT.route}:`, true, 12, 16);
            drawWrapped(simplified.route || TXT.empty, false, 11, 32);
          } else {
            const payloadObj = payload as Record<string, unknown>;
            const components = Array.isArray(payloadObj.components) ? payloadObj.components : [];
            const posology = payloadObj.posology && typeof payloadObj.posology === "object"
              ? payloadObj.posology as Record<string, unknown>
              : {};
            const product = payloadObj.product && typeof payloadObj.product === "object"
              ? payloadObj.product as Record<string, unknown>
              : {};
            const pharmacy = normalizeValue(payloadObj.pharmacy);
            const route = normalizeValue(payloadObj.route);

            if (components.length > 0) {
              drawWrapped(`${TXT.composition}:`, true, 12, 16);
              components.forEach((c, compIdx) => {
                if (c && typeof c === "object") {
                  const comp = c as Record<string, unknown>;
                  const name = normalizeValue(comp.name);
                  const amount = normalizeValue(comp.amount);
                  const unit = normalizeValue(comp.unit);
                  if (name) {
                    const compText = `${compIdx + 1}. ${name}${amount || unit ? ` — ${[amount, unit].filter(Boolean).join(" ")}` : ""}`;
                    drawWrapped(compText, false, 11, 32);
                  }
                }
              });
              cursor -= 4;
            }

            const dosage = normalizeValue(posology.dosage);
            const measure = normalizeValue(posology.measure);
            const freqValue = normalizeValue(posology.frequency_value);
            const freqUnit = normalizeValue(posology.frequency_unit);
            const durValue = normalizeValue(posology.duration_value);
            const durUnit = normalizeValue(posology.duration_unit);
            if (dosage && measure && freqValue && freqUnit && durValue && durUnit) {
              drawWrapped(`${TXT.posology}:`, true, 12, 16);
              const posText = `Dar ${dosage} ${measure} a cada ${freqValue} ${freqUnit.toLowerCase()} por ${durValue} ${durUnit.toLowerCase()}`;
              drawWrapped(posText, false, 11, 32);
              cursor -= 4;
            }

            const prodType = normalizeValue(product.type);
            const prodQuantity = normalizeValue(product.quantity);
            if (prodType || prodQuantity) {
              drawWrapped(`${TXT.finalProduct}:`, true, 12, 16);
              const prodText = `${prodType || ""}${prodQuantity ? ` — ${prodQuantity}` : ""}`;
              drawWrapped(prodText, false, 11, 32);
              if (pharmacy) {
                drawKeyValue(TXT.pharmacy, pharmacy, 32);
              }
              if (route) {
                drawKeyValue(TXT.route, route, 32);
              }
            }
          }
        }
      } else if (presc.type === "exam" && presc.medical_prescription_exam_items?.length) {
        presc.medical_prescription_exam_items.forEach((item, itemIdx) => {
          const itemName = item.exam_name || TXT.exam;
          drawWrapped(`${itemIdx + 1}. ${itemName}`, true, 12, 16);
          if (hasValue(item.instructions)) {
            drawKeyValue(TXT.instructions, item.instructions, 32);
          }
        });
      } else if (presc.medical_prescription_items && presc.medical_prescription_items.length > 0) {
        presc.medical_prescription_items.forEach((item, itemIdx) => {
          const itemName = item.medication_name || item.name || TXT.medication;
          const concentration = item.medication_concentration;
          const nameWithConc = concentration ? `${itemName} (${concentration})` : itemName;
          drawWrapped(`${itemIdx + 1}. ${nameWithConc}`, true, 12, 16);
          if (hasValue(item.presentation)) {
            drawKeyValue(TXT.presentation, item.presentation, 32);
          }
          if (hasValue(item.administration_route || item.route)) {
            drawKeyValue(TXT.route, item.administration_route || item.route, 32);
          }
          const posologyText = buildPosologySummary(item, (key, options) => i18n.t(`prescriptions:print.${key}`, options));
          if (hasValue(posologyText)) {
            drawKeyValue(TXT.instructions, posologyText, 32);
          }
        });
      }

      if (hasValue(presc.observations)) {
        cursor -= 4;
        drawWrapped(`${TXT.observations}:`, true, 12, 16);
        drawWrapped(normalizeValue(presc.observations), false, 12, 16);
      }
    });
  }

  // Audit Log
  if (versions && versions.length > 0) {
    cursor -= 24;
    drawDivider();
    cursor -= 4;
    drawSectionTitle(TXT.changesHistory);

    const auditLog = generateAuditLog(versions, record);

    if (auditLog.length === 0) {
      drawWrapped(TXT.emptyAudit, false, 12);
    } else {
      auditLog.forEach((entry, idx) => {
        if (idx > 0) {
          cursor -= 8;
        }
        drawWrapped(`${entry.date} - ${entry.author} (${entry.action})`, true, 12);
        entry.changes.forEach((change) => {
          drawWrapped(`  • ${change}`, false, 11, 16);
        });
      });
    }
  }

  // Seção de verificação do export
  cursor -= 24;
  drawDivider();
  cursor -= 4;
  drawSectionTitle(TXT.exportVerification);
  drawWrapped(TXT.exportVerificationText, false, 11);
  cursor -= 4;
  if (exportId) {
    drawWrapped(`${TXT.exportId}: ${exportId}`, true, 11);
  }
  if (verificationUrl) {
    drawWrapped(TXT.verifyAt, false, 10);
    drawWrapped(verificationUrl, false, 9, 16);
  }
  cursor -= 4;
  drawWrapped(TXT.validateIntegrity, false, 10);
  drawWrapped(TXT.validateStep1, false, 10, 16);
  drawWrapped(TXT.validateStep2, false, 10, 16);
  cursor -= 4;
  const exportDate = formatDateTimeBR(new Date().toISOString());
  if (exportDate && exportDate !== "-") {
    drawWrapped(`${TXT.exportedAt}: ${exportDate}`, false, 9);
  }

  // Assinatura
  if (hasValue(vetName) || hasValue(vetCrmv)) {
    cursor -= 24;
    const signatureY = cursor;
    const signatureWidth = 200;
    const signatureX = width - PAGE_MARGIN - signatureWidth;

    // Linha de assinatura
    page.drawLine({
      start: { x: signatureX, y: cursor },
      end: { x: signatureX + signatureWidth, y: cursor },
      thickness: 2,
      color: rgb(0, 0, 0),
    });
    cursor -= 8;

    if (hasValue(vetName)) {
      cursor -= 8;
      const nameText = normalizeValue(vetName);
      const nameWidth = font.widthOfTextAtSize(nameText, 11);
      addBlock(page, nameText, {
        x: signatureX + (signatureWidth - nameWidth) / 2,
        y: cursor,
        size: 11,
        font: bold,
      });
      cursor -= 11 + LINE_GAP;
    }

    if (hasValue(vetCrmv)) {
      const crmvText = `CRMV-${normalizeValue(vetCrmv)}`;
      const crmvWidth = font.widthOfTextAtSize(crmvText, 10);
      addBlock(page, crmvText, {
        x: signatureX + (signatureWidth - crmvWidth) / 2,
        y: cursor,
        size: 10,
        font: font,
      });
      cursor -= 10 + LINE_GAP;
    }

    const signatureLabel = TXT.signatureStamp;
    const labelWidth = font.widthOfTextAtSize(signatureLabel, 9);
    addBlock(page, signatureLabel, {
      x: signatureX + (signatureWidth - labelWidth) / 2,
      y: cursor,
      size: 9,
      font: font,
    });
  }

  const pdfBytes = await pdfDoc.save();
  const finalHash = await sha256Hex(
    toArrayBuffer(pdfBytes)
  );

  return { pdfBytes, hash: finalHash, pageCount: pdfDoc.getPageCount() };
}

export function downloadPdf(pdfBytes: Uint8Array, fileName: string) {
  const blob = new Blob([toArrayBuffer(pdfBytes)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
